import { BASE_URL } from 'config';
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  Avatar,
  OutlinedInput,
  CircularProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import Swal from "sweetalert2";

const LoyaltySection = forwardRef(({ error, errors = {}, giftTypes = [] }, ref) => {
  const [loyaltyName, setLoyaltyName] = useState("");
  const [pointsRequired, setPointsRequired] = useState("");
  const [coupon, setCoupon] = useState("");
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [loyaltyTiers, setLoyaltyTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [selectedGiftTypes, setSelectedGiftTypes] = useState([]);
  const [fixedValue, setFixedValue] = useState("");
  const [percentValue, setPercentValue] = useState("");
  const [shippingFixedValue, setShippingFixedValue] = useState("");
  const [shippingPercentValue, setShippingPercentValue] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productDetails, setProductDetails] = useState([]);
  const [didLoadFromApi, setDidLoadFromApi] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!loyaltyName.trim()) {
      newErrors.loyaltyName = "يرجى إدخال اسم عرض الولاء";
    }
    if (!pointsRequired || isNaN(pointsRequired) || Number(pointsRequired) <= 0) {
      newErrors.pointsRequired = "يرجى إدخال عدد النقاط المطلوبة بشكل صحيح";
    }
    if (!selectedTiers || selectedTiers.length === 0) {
      newErrors.selectedTiers = "يرجى اختيار مستوى ولاء واحد على الأقل";
    }
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    getLoyaltyData: () => {
      if (!loyaltyName || !pointsRequired || selectedTiers.length === 0) {
        return null;
      }
      const promotion_gift_types = [];
      if (selectedGiftTypes.includes(1)) {
        promotion_gift_types.push({ gift_type_id: 1, value: fixedValue });
      }
      if (selectedGiftTypes.includes(2)) {
        promotion_gift_types.push({ gift_type_id: 2, value: percentValue });
      }
      if (selectedGiftTypes.includes(3)) {
        promotion_gift_types.push({ gift_type_id: 3 });
      }
      if (selectedGiftTypes.includes(4)) {
        promotion_gift_types.push({ gift_type_id: 4 });
      }
      if (selectedGiftTypes.includes(5)) {
        promotion_gift_types.push({ gift_type_id: 5, value: shippingFixedValue });
      }
      if (selectedGiftTypes.includes(6)) {
        promotion_gift_types.push({ gift_type_id: 6, value: shippingPercentValue });
      }
      if (selectedGiftTypes.includes(7)) {
        promotion_gift_types.push({ gift_type_id: 7 });
      }
      let free_products = [];
      if (selectedGiftTypes.includes(3) && productDetails.length > 0) {
        free_products = productDetails.map((p) => ({
          product_id: p.id,
          quantity: p.qty,
        }));
      }
      return {
        loyalty_offer: {
          name: loyaltyName,
          points_required: parseInt(pointsRequired),
          coupon: coupon || null,
          loyalty_tier_ids: selectedTiers.map((t) => parseInt(t)),
        },
        rewards: {
          promotion_gift_types,
          free_products,
        },
      };
    },
    reset: () => {
      setLoyaltyName("");
      setPointsRequired("");
      setCoupon("");
      setSelectedTiers([]);
      setSelectedGiftTypes([]);
      setFixedValue("");
      setPercentValue("");
      setShippingFixedValue("");
      setShippingPercentValue("");
      setSelectedProducts([]);
      setProductDetails([]);
    },
    validate,
    setLoyaltyDataFromApi: (loyaltyReward, loyaltyTiers, giftTypes) => {
      if (didLoadFromApi) return;
      setLoyaltyName(loyaltyReward?.name || "");
      setPointsRequired(loyaltyReward?.points_required || "");
      setCoupon(loyaltyReward?.coupon || "");
      setSelectedTiers(
        Array.isArray(loyaltyTiers) ? loyaltyTiers.map((t) => t.loyalty_tier_id) : []
      );
      if (giftTypes) {
        setSelectedGiftTypes(giftTypes.map((g) => g.id));
        giftTypes.forEach((g) => {
          if (g.id === 1) setFixedValue(g.relationship?.value || "");
          if (g.id === 2) setPercentValue(g.relationship?.value || "");
          if (g.id === 5) setShippingFixedValue(g.relationship?.value || "");
          if (g.id === 6) setShippingPercentValue(g.relationship?.value || "");
        });
      }
      if (loyaltyReward?.free_products && Array.isArray(loyaltyReward.free_products)) {
        setSelectedProducts(
          loyaltyReward.free_products.map((fp) => ({
            id: fp.product_id,
            name: fp.product?.name || "",
            medias: fp.product?.medias || [],
          }))
        );
        setProductDetails(
          loyaltyReward.free_products.map((fp) => ({
            id: fp.product_id,
            name: fp.product?.name || "",
            image:
              fp.product?.medias && fp.product.medias.length > 0
                ? fp.product.medias[0].file_name
                : "",
            qty: fp.quantity || 1,
          }))
        );
      }
      setDidLoadFromApi(true);
    },
  }));

  useEffect(() => {
    const fetchLoyaltyTiers = async () => {
      try {
        setLoadingTiers(true);
        const token = localStorage.getItem("user-token");
        const response = await axios.get(
          BASE_URL + "/administrator/loyalty-tiers",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.status) {
          setLoyaltyTiers(response.data.data || []);
        }
      } catch (error) {
        Swal.fire({
          title: "خطأ",
          text: "حدث خطأ أثناء جلب مستويات الولاء",
          icon: "error",
          confirmButtonText: "حسناً",
        });
      } finally {
        setLoadingTiers(false);
      }
    };
    fetchLoyaltyTiers();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        const token = localStorage.getItem("user-token");
        const response = await axios.get(
          BASE_URL + "/administrator/products",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.status) {
          setProducts(response.data.data || []);
        }
      } catch (error) {
        Swal.fire({
          title: "خطأ",
          text: "حدث خطأ أثناء جلب المنتجات",
          icon: "error",
          confirmButtonText: "حسناً",
        });
      } finally {
        setProductsLoading(false);
      }
    };
    if (selectedGiftTypes.includes(3)) {
      fetchProducts();
    }
  }, [selectedGiftTypes]);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      const details = selectedProducts.map((product) => ({
        id: product.id,
        name: product.name,
        image: product.medias && product.medias.length > 0 ? product.medias[0].file_name : "",
        qty: 1,
      }));
      setProductDetails(details);
    } else {
      setProductDetails([]);
    }
  }, [selectedProducts]);

  const handleGiftTypeChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedGiftTypes(typeof value === "string" ? value.split(",") : value);
  };

  const handleProductQtyChange = (productId, newQty) => {
    setProductDetails((prev) =>
      prev.map((p) => (p.id === productId ? { ...p, qty: parseInt(newQty) } : p))
    );
  };

  useEffect(() => {
    setDidLoadFromApi(false);
  }, [window.location.pathname]);

  return (
    <MDBox
      display="flex"
      flexDirection="column"
      gap={3}
      p={3}
      boxShadow={3}
      borderRadius="16px"
      bgcolor="#fff"
      mb={4}
      sx={{ width: "90%", margin: "0 auto" }}
    >
      <MDTypography variant="h5" fontWeight="bold" mb={2}>
        بيانات عرض الولاء
      </MDTypography>
      <MDBox display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }}>
        <MDBox flex={1}>
          <MDInput
            label="اسم عرض الولاء *"
            value={loyaltyName}
            onChange={(e) => setLoyaltyName(e.target.value)}
            error={!!errors.loyaltyName}
            fullWidth
            inputProps={{ dir: "rtl" }}
          />
          {errors.loyaltyName && (
            <Typography color="error" fontSize={13} mt={0.5}>
              {errors.loyaltyName}
            </Typography>
          )}
        </MDBox>
        <FormControl fullWidth error={!!errors.selectedTiers} sx={{ flex: 1 }}>
          <InputLabel id="loyalty-tier-label">مستوى الولاء *</InputLabel>
          <Select
            labelId="loyalty-tier-label"
            multiple
            value={selectedTiers}
            onChange={(e) => setSelectedTiers(e.target.value)}
            input={<OutlinedInput label="مستوى الولاء *" />}
            sx={{ height: 45 }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((id) => {
                  const tier = loyaltyTiers.find((t) => t.id === id);
                  return tier ? <Chip key={id} label={tier.name} sx={{ ml: 0.5 }} /> : null;
                })}
              </Box>
            )}
          >
            {loadingTiers ? (
              <MenuItem disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              loyaltyTiers.map((tier) => (
                <MenuItem key={tier.id} value={tier.id}>
                  {tier.name}
                </MenuItem>
              ))
            )}
          </Select>
          {errors.selectedTiers && (
            <Typography color="error" fontSize={13} mt={0.5}>
              {errors.selectedTiers}
            </Typography>
          )}
        </FormControl>
      </MDBox>
      <MDBox display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }}>
        <MDInput
          label="الكوبون (اختياري)"
          value={coupon}
          onChange={(e) => setCoupon(e.target.value)}
          inputProps={{ dir: "rtl" }}
        />
        <MDBox flex={1}>
          <MDInput
            label="النقاط المطلوبة *"
            type="number"
            value={pointsRequired}
            onChange={(e) => setPointsRequired(e.target.value)}
            error={!!errors.pointsRequired}
            inputProps={{ dir: "rtl" }}
          />
          {errors.pointsRequired && (
            <Typography color="error" fontSize={13} mt={0.5}>
              {errors.pointsRequired}
            </Typography>
          )}
        </MDBox>
      </MDBox>
      {/* قسم الهدايا */}
      <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={3}>
        <MDTypography variant="h6" fontWeight="bold" mb={0}>
          المكافآت
        </MDTypography>
        <FormControl>
          <InputLabel id="gift-types-label">أنواع المكافآت</InputLabel>
          <Select
            labelId="gift-types-label"
            multiple
            value={selectedGiftTypes}
            onChange={handleGiftTypeChange}
            label="أنواع المكافآت"
            sx={{ height: 40, width: 300 }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={giftTypes.find((type) => type.id === value)?.name || ""}
                    sx={{ ml: 0.5 }}
                  />
                ))}
              </Box>
            )}
          >
            {giftTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {error && (
          <Typography color="error" fontSize={13}>
            {error.message || error}
          </Typography>
        )}
        {errors.rewardsError && (
          <Typography color="error" fontSize={13}>
            {errors.rewardsError}
          </Typography>
        )}
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start">
          {selectedGiftTypes.includes(1) && (
            <MDInput
              label="قيمة الخصم الثابت"
              type="number"
              value={fixedValue}
              onChange={(e) => setFixedValue(e.target.value)}
              sx={{ minWidth: 200 }}
              inputProps={{ dir: "rtl" }}
            />
          )}
          {selectedGiftTypes.includes(2) && (
            <MDInput
              label="نسبة الخصم"
              type="number"
              value={percentValue}
              onChange={(e) => setPercentValue(e.target.value)}
              sx={{ minWidth: 200 }}
              inputProps={{ dir: "rtl" }}
            />
          )}
          {selectedGiftTypes.includes(5) && (
            <MDInput
              label="قيمة خصم الشحن الثابت"
              type="number"
              value={shippingFixedValue}
              onChange={(e) => setShippingFixedValue(e.target.value)}
              sx={{ minWidth: 200 }}
              inputProps={{ dir: "rtl" }}
            />
          )}
          {selectedGiftTypes.includes(6) && (
            <MDInput
              label="نسبة خصم الشحن"
              type="number"
              value={shippingPercentValue}
              onChange={(e) => setShippingPercentValue(e.target.value)}
              sx={{ minWidth: 200 }}
              inputProps={{ dir: "rtl" }}
            />
          )}
        </Box>
        {selectedGiftTypes.includes(3) && (
          <MDBox mt={2} width="100%">
            <MDTypography variant="subtitle1" fontWeight="bold" mb={1}>
              المنتجات المجانية
            </MDTypography>
            <Autocomplete
              multiple
              options={products}
              loading={productsLoading}
              getOptionLabel={(option) => option.name || ""}
              value={selectedProducts}
              onChange={(_, newValue) => setSelectedProducts(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Avatar
                    src={
                      option.medias && option.medias.length > 0 ? option.medias[0].file_name : ""
                    }
                    alt={option.name}
                    sx={{ width: 32, height: 32 }}
                  />
                  <Typography variant="body2">{option.name}</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <MDInput
                  {...params}
                  label="اختر المنتجات المجانية"
                  fullWidth
                  inputProps={{ ...params.inputProps, dir: "rtl" }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {productsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ width: "100%" }}
            />
            {productDetails.length > 0 && (
              <MDBox mt={2}>
                <MDTypography variant="subtitle1" fontWeight="bold" mb={1}>
                  المنتجات المختارة:
                </MDTypography>
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                    background: "#fafafa",
                    borderRadius: 2,
                  }}
                >
                  <Box component="thead">
                    <Box component="tr" sx={{ background: "#f0f0f0" }}>
                      <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                        المنتج
                      </Box>
                      <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                        الكمية
                      </Box>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {productDetails.map((p) => (
                      <Box component="tr" key={p.id}>
                        <Box
                          component="td"
                          sx={{
                            p: 1,
                            border: "1px solid #eee",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            src={p.image}
                            alt={p.name}
                            sx={{ width: 32, height: 32, ml: 1 }}
                          />
                          <Typography variant="body2">{p.name}</Typography>
                        </Box>
                        <Box component="td" sx={{ p: 1, border: "1px solid #eee" }}>
                          <MDInput
                            type="number"
                            value={p.qty}
                            onChange={(e) => handleProductQtyChange(p.id, e.target.value)}
                            inputProps={{ min: 1, dir: "rtl" }}
                            sx={{ width: 80 }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </MDBox>
            )}
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
});

export default LoyaltySection;
